import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgApexchartsModule, ChartComponent } from 'ng-apexcharts';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { BillingService } from '../../../../services/billing.service';
import { LookupService } from '../../../../services/lookup.service';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { UserService } from '../../../../services/user.service';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { ListTableComponent } from '../../../../shared/components/list-table/list-table.component';
import { ReportFiltersComponent } from '../report-filters/report-filters.component';

@Component({
  selector: 'ppl-activity-report',
  standalone: true,
  imports: [
    NgApexchartsModule,
    NzCheckboxModule,
    CommonModule,
    FormsModule,
    NzCardModule,
    NzGridModule,
    NzSelectModule,
    NzTableModule,
    NzRadioModule,
    ListTableComponent,
    ReportFiltersComponent,
  ],
  templateUrl: './activity-report.component.html',
  styleUrl: './activity-report.component.css',
})
export class ActivityReportComponent implements OnInit {
  @ViewChild('chart') chart!: ChartComponent;
  mcoList: any;
  programName: any;
  mcoName: any;
  selectedClaimType: any = 'All';
  chartOptions: any;
  activityReportData: any;
  activityReportGridData: any;
  filteredSeries: any = [];
  visibleColumns: any = [];
  metricType = 'batchDate';
  categories: any;
  xAxisLabel: any;
  filterFields = [
    { field: 'metricType', show: true, default: 'batchDate' },
    { field: 'mcoName', show: true, default: '' },
    { field: 'selectedClaimType', show: true, default: 'All' },
  ];
  cols = [
    {
      field: 'timesheetCount',
      header: 'Timesheet Count',
      jsonpath: 'timesheetCount',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'claimCount',
      header: 'Claim Count',
      jsonpath: 'claimCount',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'memberCount',
      header: 'Member Count',
      jsonpath: 'memberCount',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'totalBilled',
      header: 'Total Billed Amount',
      jsonpath: 'totalBilled',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'totalPaid',
      header: 'Total Paid Amount',
      jsonpath: 'totalPaid',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'totalDenied',
      header: 'Total Denied Amount',
      jsonpath: 'totalDenied',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'totalTimesheetPaid',
      header: 'Total Timesheet Amount',
      jsonpath: 'totalTimesheetPaid',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
  ];

  selectedFilters = [
    {
      label: 'Timesheets Count',
      value: 'timesheetCount',
      checked: true,
      disabled: false,
    },
    {
      label: 'Claims Count',
      value: 'claimCount',
      checked: false,
      disabled: false,
    },
    {
      label: 'Member Count',
      value: 'memberCount',
      checked: false,
      disabled: false,
    },
    {
      label: 'Total Billed Amount',
      value: 'totalBilled',
      checked: false,
      disabled: true,
    },
    {
      label: 'Total Paid Amount',
      value: 'totalPaid',
      checked: false,
      disabled: true,
    },
    {
      label: 'Total Denied Amount',
      value: 'totalDenied',
      checked: false,
      disabled: true,
    },
    {
      label: 'Total Timesheet Amount',
      value: 'totalTimesheetPaid',
      checked: false,
      disabled: true,
    },
  ];

  allSeries: any = [];

  constructor(
    private lookupService: LookupService,
    private billingService: BillingService,
    private userService: UserService
  ) {}

  ngOnInit() {
    const selectedProgram = this.userService.getSelectedProgram();
    this.programName = selectedProgram?.program_code ?? '';
    const navigationState = window.history.state;
    if (navigationState.type) {
      const pathType =
        navigationState.type === 'timesheetsCount'
          ? 'timesheetCount'
          : navigationState.type === 'claimsCount'
          ? 'claimCount'
          : navigationState.type;

      this.selectedFilters = this.selectedFilters.map((filter) => ({
        ...filter,
        checked: filter.value === pathType,
      }));
    }
    if (navigationState.mco) {
      this.mcoName = navigationState.mco;
    }
    if (navigationState.pmpm) {
      this.selectedClaimType = navigationState.pmpm;
    }

    this.lookupService.getOrgData(this.programName).subscribe((data: any) => {
      if (data.status === 200) {
          this.mcoList = data.responsedata.filter((item: any) => item.orgTypeLookup === 'MCO');
          this.mcoName = this.mcoList[0]?.orgName;
          this.filterFields = this.filterFields?.map(f =>
            f.field === 'mcoName' ? { ...f, default: this.mcoList[0]?.orgName } : f
          ) ?? [];
          this.getActivityData(this.mcoName, this.selectedClaimType);
      }
    });
    this.updateVisibleColumns();
  }

  filterChanged(change: any) {
    this.mcoName = change.formValues.mcoName;
    this.selectedClaimType = change.formValues.selectedClaimType;
    this.metricType = change.formValues.metricType;
    this.getActivityData(this.mcoName, this.selectedClaimType);
  }

  setCategories() {
    if (this.metricType === 'monthName') {
      this.xAxisLabel = 'Month';
      this.categories = this.activityReportData?.map(
        (item: any) => item.monthName
      );
    } else if (this.metricType === 'payrollEndDate') {
      this.xAxisLabel = 'Payroll End Date';
      this.categories = this.activityReportData?.map(
        (item: any) => item.payrollEndDate
      );
    } else {
      this.xAxisLabel = 'Batch Date';
      this.categories = this.activityReportData?.map(
        (item: any) => item.batchDate
      );
    }
  }

  getActivityData(mco: string, claimType: string) {
    const options = {
      reportName: 'fetchActivityReport',
      splitBy:
        this.metricType === 'monthName'
          ? 'MONTHLY'
          : this.metricType.toUpperCase(),
      mcoName: mco,
      claimType: claimType,
      programCode: this.programName,
    };
    this.billingService.getReports(options).subscribe((data: any) => {
      this.activityReportData = data.responsedata || [];
      this.activityReportGridData = JSON.parse(
        JSON.stringify(this.activityReportData)
      );
      this.activityReportGridData.forEach((element: any) => {
        element.totalBilled = element.totalBilled
          ? this.billingService.formatCurrency(element.totalBilled)
          : element.totalBilled;
        element.totalPaid = element.totalPaid
          ? this.billingService.formatCurrency(element.totalPaid)
          : element.totalPaid;
        element.totalDenied = element.totalDenied
          ? this.billingService.formatCurrency(element.totalDenied)
          : element.totalDenied;
        element.totalTimesheetPaid = element.totalTimesheetPaid
          ? this.billingService.formatCurrency(element.totalTimesheetPaid)
          : element.totalTimesheetPaid;
      });
      this.setCategories();
      this.fetchChartOptions();
      this.createAllSeries();
      this.onFilterChange(this.selectedFilters);
    });
  }

  fetchChartOptions() {
    this.chartOptions = {
      series: [],
      chart: {
        height: 400,
        type: 'line',
        offsetY: 20,
        toolbar: {
          show: true,
          tools: {
            download: true,
            selection: false,
            zoom: false,
            zoomin: false,
            zoomout: false,
            pan: false,
            reset: false,
          },
        },
      },
      colors: [
        '#4DB6AC',
        '#77B6EA',
        '#545454',
        '#FFA726',
        '#AED581',
        '#BA68C8',
        '#FDD835',
      ],
      dataLabels: {
        enabled: true,
        formatter: (val: any, opts: any) => {
          if (val == null) {
            return '';
          }
          const seriesName = opts.w.config.series[opts.seriesIndex].name;
          if (
            seriesName === 'Total Billed Amount' ||
            seriesName === 'Total Paid Amount' ||
            seriesName === 'Total Denied Amount' ||
            seriesName === 'Total Timesheet Amount'
          ) {
            return this.billingService.formatCurrency(val);
          }
          return val.toString();
        },
      },
      stroke: {
        width: 2,
      },
      title: {
        text: '',
        align: 'left',
      },
      grid: {
        borderColor: '#e7e7e7',
        row: {
          colors: ['#f3f3f3', 'transparent'],
          opacity: 0.5,
        },
      },
      markers: {
        size: 1,
      },
      xaxis: {
        categories: this.categories,
        title: {
          text: this.xAxisLabel,
        },
      },
      yaxis: {},
      tooltip: {
        enabled: true,
        followCursor: true,
        y: {
          formatter: (value: number, opts: any) => {
            const seriesName = opts.w.config.series[opts.seriesIndex]?.name;
            if (seriesName.toLowerCase().includes('count')) {
              return value.toLocaleString();
            } else {
              return this.billingService.formatCurrency(value);
            }
          },
        },
      },
      legend: {
        position: 'top',
        horizontalAlign: 'right',
        floating: true,
        offsetY: -5,
      },
      responsive: [
        {
          breakpoint: 768, // applies when screen width <= 768px
          options: {
            chart: {
              height: 300,
            },
            legend: {
              position: 'bottom',
            },
            xaxis: {
              labels: {
                rotate: -45,
                style: {
                  fontSize: '10px',
                },
              },
            },
            dataLabels: {
              style: {
                fontSize: '6px',
              },
            },
          },
        },
      ],
    };
  }

  onFilterChange(selected: any) {
    const groupA = ['timesheetCount', 'claimCount', 'memberCount'];
    const groupB = [
      'totalBilled',
      'totalPaid',
      'totalDenied',
      'totalTimesheetPaid',
    ];
    const isGroupAChecked = selected.some(
      (f: any) => groupA.includes(f.value) && f.checked
    );
    const isGroupBChecked = selected.some(
      (f: any) => groupB.includes(f.value) && f.checked
    );

    this.selectedFilters = this.selectedFilters.map((filter: any) => {
      const inGroupA = groupA.includes(filter.value);
      const inGroupB = groupB.includes(filter.value);

      return {
        ...filter,
        disabled:
          (isGroupAChecked && inGroupB) || (isGroupBChecked && inGroupA),
      };
    });

    const selectedItem = selected
      .filter((item: any) => item.checked)
      .map((item: any) => item.value);
    if (selectedItem) {
      this.updateChart(selectedItem);
    }
    this.updateVisibleColumns();
  }

  updateVisibleColumns(): void {
    const selectedFields = this.selectedFilters
      .filter((filter) => filter.checked)
      .map((filter) => filter.value);
    this.visibleColumns = this.cols.filter((col) =>
      selectedFields.includes(col.field)
    );

    let dynamicColumn: any = null;
    switch (this.metricType) {
      case 'batchDate':
        dynamicColumn = {
          field: 'batchDate',
          header: 'Batch Date',
          jsonpath: 'batchDate',
          isDisplayedAtHeader: 'y',
          isSearchable: 'n',
        };
        break;
      case 'payrollEndDate':
        dynamicColumn = {
          field: 'payrollEndDate',
          header: 'Payroll End Date',
          jsonpath: 'payrollEndDate',
          isDisplayedAtHeader: 'y',
          isSearchable: 'n',
        };
        break;
      case 'monthName':
        dynamicColumn = {
          field: 'monthName',
          header: 'Month',
          jsonpath: 'monthName',
          isDisplayedAtHeader: 'y',
          isSearchable: 'n',
        };
        break;
    }

    if (dynamicColumn) {
      this.visibleColumns.unshift(dynamicColumn);
    }
  }

  getLabel(value: string): string {
    const match = this.selectedFilters.find((f) => f.value === value);
    return match ? match.label : value;
  }

  hasVisibleColumns(): boolean {
    return this.selectedFilters.some((filter) => filter.checked);
  }

  createAllSeries() {
    this.allSeries = [
      {
        name: 'Timesheets Count',
        data: this.activityReportData?.map((item: any) => item.timesheetCount),
        id: 'timesheetCount',
      },
      {
        name: 'Claims Count',
        data: this.activityReportData?.map((item: any) => item.claimCount),
        id: 'claimCount',
      },
      {
        name: 'Member Count',
        data: this.activityReportData?.map((item: any) => item.memberCount),
        id: 'memberCount',
      },
      {
        name: 'Total Billed Amount',
        data: this.activityReportData?.map((item: any) => item.totalBilled),
        id: 'totalBilled',
      },
      {
        name: 'Total Paid Amount',
        data: this.activityReportData?.map((item: any) => item.totalPaid),
        id: 'totalPaid',
      },
      {
        name: 'Total Denied Amount',
        data: this.activityReportData?.map((item: any) => item.totalDenied),
        id: 'totalDenied',
      },
      {
        name: 'Total Timesheet Amount',
        data: this.activityReportData?.map(
          (item: any) => item.totalTimesheetPaid
        ),
        id: 'totalTimesheetPaid',
      },
    ];
  }

  updateChart(type: any) {
    this.filteredSeries = this.allSeries.filter((series: any) =>
      type.includes(series.id)
    );
    this.chartOptions = {
      ...this.chartOptions,
      series: [...this.filteredSeries],
      tooltip: {
        enabled: true,
        followCursor: true,
        y: {
          formatter: (value: number, opts: any) => {
            const seriesName = opts.w.config.series[opts.seriesIndex]?.name;
            if (seriesName.toLowerCase().includes('count')) {
              return value.toLocaleString();
            } else {
              return this.billingService.formatCurrency(value);
            }
          },
        },
      },
    };
  }
}
